/* assets/setup-builder.js
   Setup Builder

   Features:
   - Tier switching
   - Vivarium row per tier: swatches update variant/price/rrp/thumb/title/inventory
   - Cabinet row per tier: auto-matches vivarium colour/variant title
   - Out of stock rows are unticked and disabled
   - Totals + RRP + saving in sidebar
   - Tier button price = sum of ticked items inside that tier panel (viv row included)
   - Consent-safe analytics helper (Shopify Customer Privacy)
   - Order-level cart attributes:
        Setup Builder Summary
        Setup Builder Click ID
        Setup Builder Clicked At
*/

(() => {
  const root = document.querySelector('[data-setup-builder]');
  if (!root) return;

  const tierBtns = Array.from(root.querySelectorAll('[data-tier-btn]'));
  const tierPanels = Array.from(root.querySelectorAll('[data-tier-panel]'));

  const totalEl = root.querySelector('[data-total-money]');
  const totalRrpEl = root.querySelector('[data-total-rrp]');
  const totalSaveEl = root.querySelector('[data-total-save]');
  const statusEl = root.querySelector('[data-status]');
  const addBtn = root.querySelector('[data-add-bundle]');

  // ---------- utils ----------
  const clampInt = (v, fallback = 0) => {
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const toBoolAttr = (v) => String(v) === 'true';

  const moneyFormat =
    (window.Shopify && typeof window.Shopify.money_format === 'string' && window.Shopify.money_format) || null;

  const fmt = (cents) => {
    const safe = Number(cents) || 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(safe, moneyFormat || undefined);
      } catch (_) {}
    }
    const currency = document.documentElement.getAttribute('data-shopify-currency') || 'GBP';
    return (safe / 100).toLocaleString(undefined, { style: 'currency', currency });
  };

  const safeText = (str) => String(str || '').replace(/\s+/g, ' ').trim();

  function uuidLike(prefix = 'setup_builder') {
    const rand = Math.random().toString(16).slice(2);
    return `${prefix}_${Date.now().toString(16)}_${rand}`;
  }

  function getActiveTierIndex() {
    const active = root.querySelector('[data-tier-btn][aria-selected="true"]');
    return active ? clampInt(active.getAttribute('data-tier-index'), 0) : 0;
  }

  function getTierPanel(index) {
    return root.querySelector(`[data-tier-panel][data-tier-index="${index}"]`);
  }

  function getActiveTierPanel() {
    return getTierPanel(getActiveTierIndex());
  }

  function getTierName(index) {
    const panel = getTierPanel(index);
    if (!panel) return '';

    const explicit = panel.getAttribute('data-tier-name');
    if (explicit) return safeText(explicit);

    const btn = root.querySelector(`[data-tier-btn][data-tier-index="${index}"]`);
    const btnName = btn?.getAttribute('data-tier-name');
    if (btnName) return safeText(btnName);

    const label = btn?.querySelector('.setup-builder__tier-btn-text')?.textContent;
    return safeText(label);
  }

  function getCheckbox(row) {
    return row ? row.querySelector('[data-item-checkbox]') : null;
  }

  function setRowCheckedState(row, checked, disabled = false) {
    if (!row) return;
    const cb = getCheckbox(row);
    if (!cb) return;

    cb.checked = !!checked;
    cb.disabled = !!disabled;
  }

  function rowIsAvailable(row) {
    return row && row.getAttribute('data-available') !== 'false';
  }

  function enforceRowAvailabilityState(row) {
    if (!row) return;
    const cb = getCheckbox(row);
    if (!cb) return;

    const available = rowIsAvailable(row);
    if (!available) {
      cb.checked = false;
      cb.disabled = true;
    } else {
      // Re-enable unless it is explicitly required-disabled elsewhere
      if (!row.hasAttribute('data-required') || row.getAttribute('data-required') !== 'true') {
        cb.disabled = false;
      }
    }
  }

  function enforceAllOutOfStockRows(rootEl) {
    (rootEl || root).querySelectorAll('[data-item-row]').forEach((row) => {
      enforceRowAvailabilityState(row);
    });
  }

  // ---------- tier button price ----------
  function tierPanelTotal(panel) {
    if (!panel) return 0;
    let total = 0;

    panel.querySelectorAll('[data-item-row]').forEach((row) => {
      const cb = row.querySelector('[data-item-checkbox]');
      if (!cb || cb.disabled || !cb.checked) return;
      if (!rowIsAvailable(row)) return;

      const qty = clampInt(row.getAttribute('data-qty'), 1);
      const price = clampInt(row.getAttribute('data-price'), 0);
      total += price * qty;
    });

    return total;
  }

  function updateTierButtonPrice(index) {
    const btn = root.querySelector(`[data-tier-btn][data-tier-index="${index}"]`);
    const panel = getTierPanel(index);
    if (!btn || !panel) return;

    const priceEl = btn.querySelector('[data-tier-price]');
    if (!priceEl) return;

    priceEl.textContent = fmt(tierPanelTotal(panel));
  }

  function updateAllTierButtonPrices() {
    tierPanels.forEach((panel) => {
      const idx = panel.getAttribute('data-tier-index');
      if (idx != null) updateTierButtonPrice(idx);
    });
  }

  // ---------- inventory line ----------
  function updateInventoryLine(invLine, { available, invMgmt, invQty, threshold }) {
    if (!invLine) return;

    const t = Number(threshold) || 16;
    const qty = Number(invQty) || 0;
    const low = invMgmt === 'shopify' && qty > 0 && qty <= t;

    invLine.classList.toggle('inventory--low', low);
    invLine.classList.toggle('inventory--out', !available);

    const textEl = invLine.querySelector('[data-product-inventory]');
    if (!textEl) return;

    if (!available) {
      textEl.textContent = 'Out of stock';
    } else if (low) {
      textEl.textContent = `Low stock - ${qty} item${qty === 1 ? '' : 's'} left`;
    } else {
      textEl.textContent = 'In stock, ready to ship';
    }
  }

  // ---------- cabinet sync ----------
  function syncCabinetVariant(panel, vivariumVariantTitle) {
    if (!panel) return;

    const cabRow = panel.querySelector('[data-cabinet-row]');
    if (!cabRow) return;

    const variantsJson = cabRow.getAttribute('data-variants');
    if (!variantsJson) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsJson);
    } catch (_) {
      return;
    }

    if (!Array.isArray(variants) || !variants.length) return;

    const wanted = safeText(vivariumVariantTitle).toLowerCase();

    // Try exact title match first
    let match = variants.find((v) => safeText(v.title).toLowerCase() === wanted);

    // Fallback: contains match either direction
    if (!match && wanted) {
      match = variants.find((v) => {
        const t = safeText(v.title).toLowerCase();
        return t.includes(wanted) || wanted.includes(t);
      });
    }

    // Fallback: first variant
    if (!match) match = variants[0];
    if (!match) return;

    const available = !!match.available;
    const price = clampInt(match.price, 0);
    const rrp = clampInt(match.rrp, 0);
    const invMgmt = match.inventory_management || '';
    const invQty = clampInt(match.inventory_quantity, 0);

    cabRow.setAttribute('data-variant-id', String(match.id));
    cabRow.setAttribute('data-price', String(price));
    cabRow.setAttribute('data-rrp', String(rrp));
    cabRow.setAttribute('data-available', String(available));

    const thumbImg = cabRow.querySelector('[data-cabinet-thumb]');
    if (thumbImg && match.image) thumbImg.src = match.image;

    const variantTitleEl = cabRow.querySelector('[data-cabinet-variant-title]');
    if (variantTitleEl) variantTitleEl.textContent = match.title || '';

    const priceBox = cabRow.querySelector('[data-cabinet-price]');
    if (priceBox) {
      const hasRrp = rrp > price && rrp > 0;
      const rrpHtml = hasRrp ? `<div class="setup-builder__rrp">${fmt(rrp)}</div>` : '';
      priceBox.innerHTML = `${rrpHtml}<div class="setup-builder__sale">${fmt(price)}</div>`;
    }

    const invLine = cabRow.querySelector('[data-cabinet-inventory-line]');
    updateInventoryLine(invLine, {
      available,
      invMgmt,
      invQty,
      threshold: 16
    });

    // OOS cabinet should not be tickable
    enforceRowAvailabilityState(cabRow);
  }

  // ---------- vivarium binding per active tier ----------
  function bindVivariumForActiveTier() {
    const panel = getActiveTierPanel();
    if (!panel) return;

    const vivRow = panel.querySelector('[data-vivarium-row]');
    if (!vivRow) {
      enforceAllOutOfStockRows(panel);
      return;
    }

    const radios = Array.from(vivRow.querySelectorAll('input[type="radio"][data-vivarium-variant]'));
    if (!radios.length) {
      enforceAllOutOfStockRows(panel);
      return;
    }

    const thumbImg = vivRow.querySelector('[data-vivarium-thumb]');
    const variantTitleEl = vivRow.querySelector('[data-vivarium-variant-title]');
    const invLine = vivRow.querySelector('[data-inventory-line]');
    const priceBox = vivRow.querySelector('[data-vivarium-price]');
    const rowCheckbox = vivRow.querySelector('[data-item-checkbox]');

    const setFromRadio = (radio) => {
      const id = clampInt(radio.value, 0);
      const price = clampInt(radio.getAttribute('data-viv-price'), 0);
      const rrp = clampInt(radio.getAttribute('data-viv-rrp'), 0);
      const available = toBoolAttr(radio.getAttribute('data-viv-available'));

      vivRow.setAttribute('data-variant-id', String(id));
      vivRow.setAttribute('data-price', String(price));
      vivRow.setAttribute('data-rrp', String(rrp));
      vivRow.setAttribute('data-available', String(available));

      // OOS vivarium should not be tickable
      if (rowCheckbox && !available) {
        rowCheckbox.checked = false;
        rowCheckbox.disabled = true;
      } else if (rowCheckbox && available) {
        rowCheckbox.disabled = false;
      }

      const title = radio.getAttribute('data-viv-title') || '';
      if (variantTitleEl) variantTitleEl.textContent = title;

      const thumbUrl = radio.getAttribute('data-viv-thumb');
      if (thumbImg && thumbUrl) thumbImg.src = thumbUrl;

      if (priceBox) {
        const hasRrp = rrp > price && rrp > 0;
        const rrpHtml = hasRrp ? `<div class="setup-builder__rrp">${fmt(rrp)}</div>` : '';
        priceBox.innerHTML = `${rrpHtml}<div class="setup-builder__sale">${fmt(price)}</div>`;
      }

      const invMgmt = radio.getAttribute('data-viv-inv-mgmt') || '';
      const invQty = clampInt(radio.getAttribute('data-viv-inv-qty'), 0);
      const threshold = clampInt(radio.getAttribute('data-inv-threshold'), 16);
      updateInventoryLine(invLine, { available, invMgmt, invQty, threshold });

      // Sync cabinet to this vivarium colour
      syncCabinetVariant(panel, title);

      // Enforce all OOS rows in this tier after change
      enforceAllOutOfStockRows(panel);
    };

    const checked = radios.find((r) => r.checked && !r.disabled) || radios.find((r) => !r.disabled);
    if (checked) {
      checked.checked = true;
      setFromRadio(checked);
    }

    radios.forEach((r) => {
      r.onchange = () => {
        if (!r.checked) return;
        setFromRadio(r);
        recalc();
        updateTierButtonPrice(getActiveTierIndex());
      };
    });

    if (rowCheckbox) {
      rowCheckbox.onchange = () => {
        recalc();
        updateTierButtonPrice(getActiveTierIndex());
      };
    }

    // Initial enforcement
    enforceAllOutOfStockRows(panel);
  }

  // ---------- totals & selections ----------
  function getSelectedItems() {
    const items = [];
    const panel = getActiveTierPanel();
    if (!panel) return items;

    panel.querySelectorAll('[data-item-row]').forEach((row) => {
      const cb = row.querySelector('[data-item-checkbox]');
      if (!cb || cb.disabled || !cb.checked) return;
      if (!rowIsAvailable(row)) return;

      const id = clampInt(row.getAttribute('data-variant-id'), 0);
      if (!id) return;

      const quantity = clampInt(row.getAttribute('data-qty'), 1);
      const price = clampInt(row.getAttribute('data-price'), 0);
      const rrp = clampInt(row.getAttribute('data-rrp'), 0);

      const title =
        safeText(row.querySelector('.setup-builder__title a')?.textContent) ||
        safeText(row.querySelector('.setup-builder__title')?.textContent);

      items.push({ id, quantity, price, rrp, title });
    });

    root.querySelectorAll('[data-extras] [data-item-row]').forEach((row) => {
      const cb = row.querySelector('[data-item-checkbox]');
      if (!cb || cb.disabled || !cb.checked) return;
      if (!rowIsAvailable(row)) return;

      const id = clampInt(row.getAttribute('data-variant-id'), 0);
      if (!id) return;

      const quantity = clampInt(row.getAttribute('data-qty'), 1);
      const price = clampInt(row.getAttribute('data-price'), 0);
      const rrp = clampInt(row.getAttribute('data-rrp'), 0);

      const title =
        safeText(row.querySelector('.setup-builder__title a')?.textContent) ||
        safeText(row.querySelector('.setup-builder__title')?.textContent);

      items.push({ id, quantity, price, rrp, title });
    });

    return items;
  }

  function recalc() {
    const items = getSelectedItems();

    const total = items.reduce((sum, it) => sum + it.price * it.quantity, 0);

    const totalRrp = items.reduce((sum, it) => {
      const effective = it.rrp && it.rrp > it.price ? it.rrp : it.price;
      return sum + effective * it.quantity;
    }, 0);

    const save = Math.max(0, totalRrp - total);

    if (totalEl) totalEl.textContent = fmt(total);
    if (totalRrpEl) totalRrpEl.textContent = fmt(totalRrp);
    if (totalSaveEl) totalSaveEl.textContent = fmt(save);
  }

  // ---------- consent-safe analytics ----------
  async function analyticsAllowed() {
    try {
      if (window.Shopify?.customerPrivacy?.getTrackingConsent) {
        const consent = await window.Shopify.customerPrivacy.getTrackingConsent();
        return !!consent?.analytics;
      }
    } catch (_) {}
    return false;
  }

  async function publishAnalyticsEvent(eventName, payload) {
    const allowed = await analyticsAllowed();
    if (!allowed) return;

    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(eventName, payload);
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...payload });
    } catch (_) {}
  }

  // ---------- cart attributes ----------
  async function setCartAttributes(attrs) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      });
      return true;
    } catch (_) {
      return false;
    }
  }

  function buildBundleSummary() {
    const tierIdx = getActiveTierIndex();
    const tierName = getTierName(tierIdx);

    const items = getSelectedItems();
    const total = items.reduce((sum, it) => sum + it.price * it.quantity, 0);

    const includedParts = [];
    items.forEach((it) => {
      const name = safeText(it.title);
      if (!name) return;
      includedParts.push(`${name} ×${it.quantity}`);
    });

    const included = includedParts.join(', ');

    return [
      `Tier: ${tierName || 'Selected tier'}`,
      `Total: ${fmt(total)}`,
      `Included: ${included || '—'}`
    ].join('\n');
  }

  async function addToCart() {
    const payloadItems = getSelectedItems().map((it) => ({ id: it.id, quantity: it.quantity }));

    if (!payloadItems.length) {
      if (statusEl) statusEl.textContent = 'Please select at least one item.';
      return;
    }

    if (statusEl) statusEl.textContent = 'Adding to basket…';

    const clickId = uuidLike('setup_builder_mkpdvn5u');
    const clickedAt = new Date().toISOString();
    const summary = buildBundleSummary();

    await setCartAttributes({
      'Setup Builder Summary': summary,
      'Setup Builder Click ID': clickId,
      'Setup Builder Clicked At': clickedAt
    });

    publishAnalyticsEvent('setup_builder_add_to_cart', {
      setup_builder_click_id: clickId,
      tier_index: getActiveTierIndex(),
      tier_name: getTierName(getActiveTierIndex()),
      items: payloadItems
    });

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payloadItems })
      });

      if (!res.ok) throw new Error(await res.text());

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));

      window.location.href = '/cart';
    } catch (e) {
      console.error(e);
      if (statusEl) statusEl.textContent = 'Sorry — couldn’t add the selected items. Please try again.';
    }
  }

  // ---------- wiring ----------
  tierBtns.forEach((btn) => {
    btn.onclick = () => {
      const idx = btn.getAttribute('data-tier-index');

      tierBtns.forEach((b) => b.setAttribute('aria-selected', b === btn ? 'true' : 'false'));
      tierPanels.forEach((p) => (p.hidden = p.getAttribute('data-tier-index') !== idx));

      bindVivariumForActiveTier();
      enforceAllOutOfStockRows(getActiveTierPanel());
      recalc();
      updateTierButtonPrice(idx);
    };
  });

  root.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-item-checkbox]')) return;

    recalc();
    updateTierButtonPrice(getActiveTierIndex());
  });

  if (addBtn) addBtn.onclick = addToCart;

  // Initial
  bindVivariumForActiveTier();
  enforceAllOutOfStockRows(root);
  recalc();
  updateAllTierButtonPrices();
})();